import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { KeyRound, Plus, Search, Edit2, Trash2, X } from 'lucide-react';

const API = 'https://api.ctexpresskh.com/api';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', address: '', city: '', note: '' });
  const token = localStorage.getItem('token');
  const [resetModal, setResetModal] = useState(null);
  const [newPwd, setNewPwd] = useState('');

  const resetPassword = async () => {
    if (!newPwd || newPwd.length < 6) { alert('Min 6 characters'); return; }
    try {
      const res = await fetch('https://api.ctexpresskh.com/api/customer/admin-reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ phone: resetModal.phone, new_password: newPwd })
      });
      const data = await res.json();
      if (data.success) { alert('Password reset successfully!'); setResetModal(null); setNewPwd(''); }
      else alert('Error: ' + data.message);
    } catch { alert('Connection error'); }
  };
  const limit = 50;

  const load = (p = page, s = search) => {
    const params = new URLSearchParams({ page: p, limit });
    if (s) params.append('search', s);
    fetch(API + '/customers?' + params, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => {
        setCustomers(d.data || []);
        setTotal(d.total || 0);
      });
  };

  useEffect(() => { load(); }, [page]);

  const handleSearch = () => { setPage(1); load(1, search); };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', phone: '', address: '', city: '', note: '' });
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone, address: c.address || '', city: c.city || '', note: c.note || '' });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name || !form.phone) { toast.error('Name and phone required'); return; }
    try {
      const url = editing ? API + '/customers/' + editing.id : API + '/customers';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editing ? 'Customer updated!' : 'Customer created! ID: ' + data.data.customer_code);
        setShowModal(false); load();
      } else toast.error(data.message);
    } catch { toast.error('Server error'); }
  };

  const remove = async (c) => {
    if (!window.confirm('Delete ' + c.name + '?')) return;
    try {
      await fetch(API + '/customers/' + c.id, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
      toast.success('Deleted!'); load();
    } catch { toast.error('Error'); }
  };

  const inp = (label, key, placeholder='', type='text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#1e293b' }}>Customers</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Total: {total} customers</p>
        </div>
        <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}>
          <Plus size={18} /> New Customer
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, display: 'flex', gap: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
          <Search size={16} color="#94a3b8" />
          <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search by ID, name or phone..."
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 14 }} />
        </div>
        <button onClick={handleSearch} style={{ padding: '8px 20px', background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Search</button>
        <button onClick={() => { setSearch(''); setPage(1); load(1, ''); }} style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Clear</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              {['ID', 'Name', 'Phone', 'City', 'Address', 'Action'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No customers found</td></tr>
            ) : customers.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding: '10px 16px', fontWeight: 700, color: '#f97316' }}>{c.customer_code}</td>
                <td style={{ padding: '10px 16px', fontWeight: 600 }}>{c.name}</td>
                <td style={{ padding: '10px 16px' }}>{c.phone}</td>
                <td style={{ padding: '10px 16px', color: '#64748b' }}>{c.city || '-'}</td>
                <td style={{ padding: '10px 16px', color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.address || '-'}</td>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setResetModal(c); setNewPwd(''); }} style={{ background: '#dcfce7', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{fontSize:11}}>🔑</span>
                    </button>
                    <button onClick={() => openEdit(c)} style={{ background: '#dbeafe', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Edit2 size={13} /> Edit
                    </button>
                    <button onClick={() => remove(c)} style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Showing {((page-1)*limit)+1}–{Math.min(page*limit, total)} of {total} customers
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', background: '#fff', fontSize: 12 }}>«</button>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 13, color: '#1e293b', fontWeight: 600 }}>Page {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', background: '#fff' }}>Next</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', background: '#fff', fontSize: 12 }}>»</button>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b' }}>{editing ? 'Edit Customer' : 'New Customer'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            {editing && <div style={{ background: '#fff7ed', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#92400e', fontWeight: 600 }}>ID: {editing.customer_code}</div>}
            {inp('Full Name *', 'name', 'Full name')}
            {inp('Phone *', 'phone', 'Phone number')}
            {inp('Address', 'address', 'Address in Cambodia')}
            {inp('City', 'city', 'e.g. Phnom Penh')}
            {inp('Note', 'note', 'Optional note')}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: 10, border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' }}>Cancel</button>
              <button onClick={save} style={{ flex: 1, padding: 10, background: '#f97316', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:400, boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin:'0 0 8px', fontSize:18, color:'#1e293b' }}>🔑 Reset Password</h3>
            <p style={{ margin:'0 0 20px', fontSize:13, color:'#6b7280' }}>Customer: <strong>{resetModal.name}</strong> ({resetModal.phone})</p>
            <input
              type="password"
              placeholder="New password (min 6 chars)"
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              style={{ width:'100%', padding:'10px 14px', border:'1px solid #d1d5db', borderRadius:8, fontSize:14, marginBottom:16, boxSizing:'border-box' }}
            />
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => { setResetModal(null); setNewPwd(''); }}
                style={{ flex:1, padding:10, border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', background:'#fff' }}>Cancel</button>
              <button onClick={resetPassword}
                style={{ flex:1, padding:10, background:'#dc2626', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700 }}>Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
